import { useQuery, useMutation } from '@tanstack/react-query';
import { GlobalApi } from '@Apis';
import { queryClient } from 'src/QueryClient';
import { PolicyTypeEnum } from '../../../Policy/utils/constants';
import {
  ApprovedUsersProps,
  DeleteApprovedUsersDto,
} from '../../../../types/globalSetting';
import { useTranslation } from 'react-i18next';

interface ApprovedSitesProps extends ApprovedUsersProps {
  siteName?: string;
  siteUrl: string;
}

const getApprovedSitesQueryKey = (policyType: PolicyTypeEnum) => [
  'approvedSites',
  policyType,
];

export const useApprovedSitesQuery = (policyType: PolicyTypeEnum) => {
  return useQuery({
    queryKey: getApprovedSitesQueryKey(policyType),
    queryFn: async () => {
      const { data } = await GlobalApi.getApprovedUsers(policyType);
      return data.map((item: any) => ({
        ...item,
        siteUrl: item.email,
        siteName: item.name,
      })) as ApprovedSitesProps[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useApprovedSitesMutations = (policyType: PolicyTypeEnum) => {
  const { t } = useTranslation(['globalSetting', 'common']);
  const queryKey = getApprovedSitesQueryKey(policyType);

  const addSitesMutation = useMutation({
    mutationFn: (users: Omit<ApprovedUsersProps, 'groupCompanyGuid'>[]) =>
      GlobalApi.addApprovedUsers(policyType, users),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.apiException.internalError'),
      });
    },
  });

  const deleteSitesMutation = useMutation({
    mutationFn: (users: DeleteApprovedUsersDto[]) =>
      GlobalApi.deleteApprovedUsers(policyType, users),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.apiException.internalError'),
      });
    },
  });

  return {
    addSitesMutation,
    deleteSitesMutation,
  };
};
